import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  apiStatuses,
  clearSpidToken,
  ClearSpidToken,
  exceptionTypes,
  fetchUserInfo,
  FetchUserInfo,
  FetchUserInfoBound,
  IExceptionAction,
  initializeSpid,
  InitializeSpid,
  InitializeSpidBound,
  logException,
  LogException,
  setRedirectUri,
  SetRedirectUri,
  SetReferer,
  setReferer,
  setSpidIdAndSigFromUrl,
  SetSpidIdAndSigFromUrl,
  setUtmCampaign,
  SetUtmCampaign,
  setUtmContent,
  SetUtmContent,
  setUtmMedium,
  SetUtmMedium,
  setUtmSource,
  SetUtmSource,
  setUtmTerm,
  SetUtmTerm,
  showTimeout,
  ShowTimeout,
  validationErrors,
} from '../actions/Actions';
import { IProductInfo, ISessionInfo, IState, IUserInfo } from '../CustomTypes';
import {
  decode,
  isValidUntilPassed,
  getRedirectUrlInLocalStorage,
  safeRedirect,
  setStorageData,
} from '../fastflowUtils';
import '../styles/shame.scss';
import ErrorMessage from './ErrorMessage';
import Recheckit from './Recheckit';
import ShoppingBasket from './ShoppingBasket';
import TimeoutMessage from './TimeoutMessage';
import 'url-polyfill';
import * as queryString from 'query-string';
import LayoutLeftColumn from './LayoutLeftColumn';
import {
  classNames,
  Display,
  Loader,
  Margin,
  LoaderSize,
  FontWeight,
} from '@snoam/pinata';
import { hot } from 'react-hot-loader/root';
import { LoggedInInfo } from './LoggedInInfo';
import CoreHeader from './CoreHeader';
import {
  addParamToPulseSptCustom,
  updatePulseUserSubStatus,
} from '../PulseUtils';
import { useEffect, useContext, PropsWithChildren } from 'react';
import Disclaimer from './Disclaimer';
import { ResponsiveContext } from '../context/ResponsiveContext';
import { useSpid } from '@snoam/mono-spid';
import ReceiptWrapper from '../page-components/ReceiptWrapper';

export interface IStateProps {
  loading?: string;
  productInfo?: IProductInfo;
  userInfo?: IUserInfo;
  exceptions?: IExceptionAction[];
  sessionInfo?: ISessionInfo;
}

export interface IDispatchProps {
  initializeSpid: InitializeSpid;
  fetchUserInfo: FetchUserInfo;
  setSpidIdAndSigFromUrl: SetSpidIdAndSigFromUrl;
  showTimeout: ShowTimeout;
  clearSpidToken: ClearSpidToken;
  setRedirectUri: SetRedirectUri;
  setReferer: SetReferer;
  setUtmSource: SetUtmSource;
  setUtmMedium: SetUtmMedium;
  setUtmCampaign: SetUtmCampaign;
  setUtmContent: SetUtmContent;
  setUtmTerm: SetUtmTerm;
  logException: LogException;
}

export interface IOwnProps {
  clientName: string;
  clientShortHandName: string;
  hostname: string;
}

export interface IProps
  extends IStateProps,
    IDispatchProps,
    IOwnProps,
    RouteComponentProps<{ productId: string }, Record<string, never>> {}

interface IQueryParams {
  spidId?: string;
  sig?: string;
  b64data?: string;
  redirectUri?: string;
  redirect_uri?: string;
  referer?: string;
  source?: string;
  medium?: string;
  content?: string;
  campaign?: string;
  term?: string;
  isredirect?: string;
}

const styleClass = {
  loader: classNames(Margin.MX_AUTO, Margin.MT_20, FontWeight.FONT_LIGHT),
  mainContent: (loading) =>
    classNames('ffContent', loading ? Display.HIDDEN : null),
};

export const App: React.FunctionComponent<IProps> = (props) => {
  const { userInfo, productInfo, children, loading, logException } = props;
  const rctParam = '';
  const { spidPrimaryEmail } = userInfo;
  const { owningClient, backgroundImageUrl } = productInfo;
  const query: IQueryParams = queryString.parse(
    props.location.search,
  ) as IQueryParams;
  const headerHeightMobile = owningClient === 'AP' ? '58px' : '57px';
  const headerHeightDesktop =
    owningClient === 'AP'
      ? '80px'
      : owningClient === 'BTI' || owningClient === 'SA'
        ? '89px'
        : '57px';
  const { mobile, isHermes } = useContext(ResponsiveContext);

  const spidContext = useSpid();
  updatePulseUserSubStatus({});

  useEffect(() => {
    if (!userInfo.sig && query.sig) {
      props.setSpidIdAndSigFromUrl(query.spidId, query.sig);
    }
    (props.initializeSpid as any as InitializeSpidBound)()
      .then((userInfo) => {
        (props.fetchUserInfo as any as FetchUserInfoBound)(
          userInfo.userId,
          userInfo.sig,
          userInfo.serverTime,
          userInfo.expiresIn,
        ).catch((res: any) => {
          const query: IQueryParams = queryString.parse(
            props.location.search,
          ) as IQueryParams;
          if (
            +res.status === 403 &&
            JSON.stringify(res).indexOf(
              validationErrors.USER_AGREEMENT_NOT_ACCEPTED,
            ) !== -1 &&
            !query.isredirect
          ) {
            logException(exceptionTypes.EXCEPTION, res);
            return 'ignore';
          }
        });
      })
      .catch((e: Error) => {
        logException(exceptionTypes.EXCEPTION, e);
        (props.fetchUserInfo as any as FetchUserInfoBound)(
          query.spidId,
          query.sig,
        ).catch((e) => {
          logException(exceptionTypes.EXCEPTION, e);
          return 'ignore';
        });
      });

    let b64data;
    if (query.b64data && query.b64data !== 'undefined') {
      b64data = decode(query.b64data);
    }
    const redirectUri = query.redirectUri
      ? query.redirectUri
      : query.redirect_uri
        ? query.redirect_uri
        : b64data?.referer?.redirectUri;
    if (redirectUri) {
      props.setRedirectUri(safeRedirect(redirectUri));
    }
    if (
      query.referer ||
      (b64data && b64data.referer && b64data.referer.referer)
    ) {
      setStorageData({
        redirectUri: redirectUri,
        referer: query.referer,
        utmCampaign: query.source,
        utmContent: query.content,
        utmMedium: query.medium,
        utmSource: query.source,
        utmTerm: query.term,
      });
      props.setReferer(query.referer ? query.referer : b64data.referer.referer);
    }
    if (query.source) {
      props.setUtmSource(query.source);
    }
    if (query.medium) {
      props.setUtmMedium(query.medium);
    }
    if (query.content) {
      props.setUtmContent(query.content);
    }
    if (query.campaign) {
      props.setUtmCampaign(query.campaign);
    }
    if (query.term) {
      props.setUtmTerm(query.term);
    }
  }, []);

  useEffect(() => {
    if (userInfo.sig && isValidUntilPassed(userInfo.validUntil)) {
      props.clearSpidToken(); // We only want to re-initialize spid once
      if (userInfo.sigFromCode) {
        props.showTimeout();
        props.history.push(
          `/products/${props.match.params.productId}/usersearch?${rctParam}`,
        );
      } else {
        props.initializeSpid();
      }
    }
    addParamToPulseSptCustom(
      'abo.ff.loginState',
      Number(spidContext.state.userId) > 0,
    );
  });

  useEffect(() => {
    if (
      userInfo.acceptedAgreement !== undefined &&
      !userInfo.acceptedAgreement &&
      !location.pathname.includes('receipt')
    ) {
      props.history.push(
        `/products/${
          props.match.params.productId
        }/acceptagreement?${getRedirectUrlInLocalStorage()}`,
      );
    }
  }, [userInfo]);

  useEffect(() => {
    if (props.productInfo.header) {
      const productName =
        props.productInfo.header + ' - ' + props.productInfo.subHeader;
      addParamToPulseSptCustom('abo.ff.productName', productName);
    }

    const isForlag = productInfo.productType === 'MAGASINER';
    addParamToPulseSptCustom('abo.ff.isForlag', isForlag);

    addParamToPulseSptCustom('abo.ff.productID', productInfo.productId);
  }, [productInfo]);

  const layoutProps = (mobile) => {
    const props = {
      coreHeaderHeight:
        mobile || window.innerWidth <= 978
          ? headerHeightMobile
          : headerHeightDesktop,
      showTopImageMobile: false,
    };
    if (!mobile) {
      Object.assign(props, {
        backgroundImage: backgroundImageUrl,
      });
    }
    return props;
  };

  const isError = props.productInfo.apiStatus === 'API_ERROR';

  return (
    <div className={`ffContainer ${owningClient}`}>
      {!isHermes && (
        <div
          style={{
            height:
              mobile || window.innerWidth <= 978
                ? headerHeightMobile
                : headerHeightDesktop,
          }}
        >
          <CoreHeader
            clientShortHandName={props.clientShortHandName}
            clientName={props.clientName}
            hostname={props.hostname}
          />
        </div>
      )}
      <ReceiptWrapper {...props}>
        <LayoutLeftColumn {...layoutProps(mobile)}>
          {spidPrimaryEmail && (
            <LoggedInInfo spidPrimaryEmail={spidPrimaryEmail} />
          )}
          {!loading && <ShoppingBasket error={isError} />}
          <ErrorMessage
            productId={parseInt(props.match.params.productId, 10)}
            clientShortHandName={props.clientShortHandName}
          />
          <TimeoutMessage />
          {loading ? (
            <div className={styleClass.loader} data-testing={'page-loader'}>
              <Loader text={'Laster innhold'} size={LoaderSize.BASE} />
            </div>
          ) : null}
          <div className={styleClass.mainContent(loading)}>{children}</div>
          <Recheckit />
          <Disclaimer
            error={isError}
            owningClientName={props.productInfo.owningClientName}
            owningClient={props.productInfo.owningClient}
            myPageUrl={props.productInfo.myPageUrl}
            loading={loading}
          />
        </LayoutLeftColumn>
      </ReceiptWrapper>
    </div>
  );
};

App.displayName = 'App';

export default connect<
  IStateProps,
  IDispatchProps,
  PropsWithChildren<IOwnProps>
>(
  (s: IState) => ({
    exceptions: s.exceptions,
    loading: Object.keys(s)
      .filter((k) => s[k].apiStatus && s[k].apiStatus === apiStatuses.LOADING)
      .map((k) => s[k].loadingText || 'Vi behandler din forespørsel...')[0],
    productInfo: s.productInfo,
    sessionInfo: s.sessionInfo,
    userInfo: s.userInfo,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        clearSpidToken,
        fetchUserInfo,
        initializeSpid,
        logException,
        setRedirectUri,
        setReferer,
        setSpidIdAndSigFromUrl,
        setUtmCampaign,
        setUtmContent,
        setUtmMedium,
        setUtmSource,
        setUtmTerm,
        showTimeout,
      },
      dispatch,
    ),
)(withRouter(hot(App)));
